import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  KeyRound, Users, Eye, TrendingUp, Plus, Lock, CheckCircle,
  Trash2, Home, MapPin, BadgeCheck, Loader2, BedDouble, Bath, Maximize2,
  Pencil, Mail, ChevronDown, ChevronUp,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ListingWizard } from '../../components/ListingWizard';

const API = process.env.REACT_APP_BACKEND_URL;
const ACCENT = '#F97316';
const ACCENT_DARK = '#0A192F';

const TIERS = [
  { name: 'Starter', key: 'starter', price: '$49/mo', listings: 5,           color: '#64748B', desc: 'Perfect for selling your home' },
  { name: 'Pro',     key: 'pro',     price: '$79/mo', listings: 20,          color: '#F97316', popular: true, desc: 'Multiple properties' },
  { name: 'Team',    key: 'team',    price: '$299/mo', listings: 'Unlimited', color: ACCENT_DARK, desc: 'Property investors' },
];

// ─── Reusable UI ──────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color, loading, onClick }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      className={`bg-white rounded-2xl border border-slate-100 p-5 shadow-sm hover:shadow-md transition-all duration-200 ${onClick ? 'cursor-pointer hover:-translate-y-0.5' : ''}`}>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: color + '18' }}>
        <Icon size={18} style={{ color }} />
      </div>
      {loading ? (
        <div className="h-7 w-12 bg-slate-100 rounded animate-pulse mb-1" />
      ) : (
        <div className="text-2xl font-black" style={{ color: ACCENT_DARK }}>{value}</div>
      )}
      <div className="text-xs text-slate-500 font-medium mt-0.5">{label}</div>
      {onClick && <div className="text-xs mt-1 font-semibold" style={{ color }}>View →</div>}
    </motion.div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active:         { label: 'Active',         bg: '#DCFCE7', color: '#16A34A' },
    sold:           { label: 'Sold',           bg: '#FEE2E2', color: '#DC2626' },
    under_contract: { label: 'Under Contract', bg: '#FEF3C7', color: '#D97706' },
  };
  const s = map[status] || map.active;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: s.bg, color: s.color }}>{s.label}</span>
  );
}

function ListingCard({ listing, onDelete, onStatusChange, onEdit }) {
  const [actioning, setActioning] = useState(false);
  const photo = listing.photos?.[0];

  const doDelete = async () => {
    if (!window.confirm('Delete this listing? This cannot be undone.')) return;
    setActioning(true);
    await onDelete(listing.id);
    setActioning(false);
  };

  const toggleSold = async () => {
    setActioning(true);
    await onStatusChange(listing.id, listing.status === 'sold' ? 'active' : 'sold');
    setActioning(false);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      data-testid={`listing-card-${listing.id}`}
      className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200">
      <div className="h-36 bg-orange-50 relative overflow-hidden">
        {photo
          ? <img src={photo} alt={listing.address} className="w-full h-full object-cover" />
          : <div className="w-full h-full flex items-center justify-center"><Home size={32} className="text-orange-200" /></div>
        }
        <div className="absolute top-2 left-2"><StatusBadge status={listing.status} /></div>
        {listing.lead_count > 0 && (
          <div className="absolute top-2 right-2 bg-emerald-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
            {listing.lead_count} lead{listing.lead_count !== 1 ? 's' : ''}
          </div>
        )}
      </div>
      <div className="p-4">
        <p className="text-xs text-slate-500 flex items-center gap-1 mb-0.5">
          <MapPin size={10} /> {listing.city}, {listing.state}
        </p>
        <p className="text-sm font-bold text-slate-800 truncate">{listing.address}</p>
        <p className="text-lg font-black mt-1" style={{ color: ACCENT }}>
          ${listing.price?.toLocaleString()}
        </p>
        <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
          <span className="flex items-center gap-1"><BedDouble size={11} />{listing.beds} bd</span>
          <span className="flex items-center gap-1"><Bath size={11} />{listing.baths} ba</span>
          <span className="flex items-center gap-1"><Maximize2 size={11} />{listing.sqft?.toLocaleString()} sqft</span>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <button onClick={toggleSold} disabled={actioning}
            data-testid={`listing-toggle-sold-${listing.id}`}
            className="flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-all"
            style={listing.status === 'sold'
              ? { background: ACCENT, color: 'white', borderColor: ACCENT }
              : { background: 'white', color: ACCENT, borderColor: ACCENT }}>
            {listing.status === 'sold' ? 'Mark Active' : 'Mark Sold'}
          </button>
          <button onClick={() => onEdit(listing)} disabled={actioning}
            data-testid={`listing-edit-${listing.id}`}
            className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition-all">
            <Pencil size={14} />
          </button>
          <button onClick={doDelete} disabled={actioning}
            data-testid={`listing-delete-${listing.id}`}
            className="p-1.5 rounded-lg border border-red-200 text-red-400 hover:bg-red-50 transition-all">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function LeadCard({ lead }) {
  const [expanded, setExpanded] = useState(false);
  const score = lead.match_score;
  const scoreColor = score >= 80 ? '#16A34A' : score >= 60 ? '#D97706' : '#64748B';
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      data-testid={`lead-card-${lead.id || lead.buyer_id}`}
      onClick={() => setExpanded(e => !e)}
      className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm text-slate-800 truncate">{lead.buyer_name || 'Anonymous Buyer'}</p>
          <p className="text-xs text-slate-500 mt-0.5 truncate">{lead.listing_address || 'Your listing'}</p>
          {lead.message && (
            <p className="text-xs text-slate-600 mt-2 bg-slate-50 p-2 rounded-lg leading-relaxed">{lead.message}</p>
          )}
        </div>
        <div className="flex items-center gap-2 ml-4 flex-shrink-0">
          {score != null && (
            <div className="text-right">
              <div className="text-xl font-black" style={{ color: scoreColor }}>{score}%</div>
              <div className="text-xs text-slate-400">match</div>
            </div>
          )}
          {expanded ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
              {lead.buyer_email && (
                <a href={`mailto:${lead.buyer_email}`} onClick={e => e.stopPropagation()}
                  data-testid={`lead-email-${lead.id || lead.buyer_id}`}
                  className="flex items-center gap-2 text-sm font-semibold hover:underline"
                  style={{ color: ACCENT }}>
                  <Mail size={13} />
                  {lead.buyer_email}
                </a>
              )}
              <p className="text-xs text-slate-400">Click email to reply directly</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {lead.contacted_at && (
        <p className="text-xs text-slate-400 mt-3">{new Date(lead.contacted_at).toLocaleDateString()}</p>
      )}
    </motion.div>
  );
}

// ─── Tabs ─────────────────────────────────────────────────────────────────────
function DashboardTab({ stats, subscription, onSubscribe, subscribing, loading }) {
  const navigate = useNavigate();
  const active = subscription?.status === 'active';
  const tierName = subscription?.tier;
  const used = stats.listing_count;
  const limit = subscription?.listing_limit;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-black" style={{ color: ACCENT_DARK }}>Seller Dashboard</h2>
          <p className="text-slate-500 text-sm mt-1">Track buyer interest and leads on your home</p>
        </div>
        {active && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold"
            style={{ background: '#FFF7ED', color: '#EA580C' }}>
            <BadgeCheck size={13} />
            {tierName?.charAt(0).toUpperCase() + tierName?.slice(1)} Plan Active
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Home}      label="My Listings"      value={stats.listing_count}  color={ACCENT}    loading={loading}
          onClick={() => navigate('/seller/listings')} />
        <StatCard icon={Users}     label="Buyer Leads"       value={stats.lead_count}      color="#10B981"   loading={loading}
          onClick={() => navigate('/seller/leads')} />
        <StatCard icon={Eye}       label="Total Views"       value={stats.total_views}     color="#6366F1"   loading={loading} />
        <StatCard icon={TrendingUp} label="Avg Match Score"  value={stats.avg_match_score ? `${stats.avg_match_score}%` : '—'} color="#F59E0B" loading={loading} />
      </div>

      {!active && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { step: '1', title: 'List your home', desc: 'Add details & photos' },
            { step: '2', title: 'Buyers find you', desc: 'Algorithm matches in real time' },
            { step: '3', title: 'Receive leads', desc: 'Instant alerts with match score' },
          ].map(s => (
            <div key={s.step} className="flex items-center gap-2.5 bg-white border border-slate-100 rounded-xl p-3 shadow-sm">
              <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-black text-white"
                style={{ background: ACCENT }}>{s.step}</div>
              <div>
                <div className="font-bold text-xs" style={{ color: ACCENT_DARK }}>{s.title}</div>
                <div className="text-xs text-slate-400">{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        {active ? (
          <>
            <h3 className="text-base font-bold mb-1" style={{ color: ACCENT_DARK }}>
              {tierName?.charAt(0).toUpperCase() + tierName?.slice(1)} Plan Active
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              {typeof limit === 'number' && limit < 9999
                ? `${used} of ${limit} listings used`
                : 'Unlimited listings available'}
            </p>
            {typeof limit === 'number' && limit < 9999 && (
              <div className="w-full bg-orange-100 rounded-full h-2 mb-4">
                <div className="h-2 rounded-full transition-all" style={{ background: ACCENT, width: `${Math.min((used / limit) * 100, 100)}%` }} />
              </div>
            )}
            <p className="text-xs text-slate-400 mb-4">Need more capacity? Switch to a higher plan.</p>
          </>
        ) : (
          <>
            <h3 className="text-lg font-bold mb-1" style={{ color: ACCENT_DARK }}>Choose your listing plan</h3>
            <p className="text-slate-500 text-sm mb-5">Subscribe to start listing. Cancel anytime.</p>
          </>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {TIERS.map(t => (
            <div key={t.key} data-testid={`seller-tier-${t.key}`}
              className={`p-5 rounded-xl border-2 transition-all duration-200 ${
                subscription?.tier === t.key ? 'border-orange-400 bg-orange-50/50' :
                t.popular ? 'border-orange-400' : 'border-slate-200 hover:border-slate-300'
              }`}>
              {t.popular && !active && <div className="text-xs font-bold text-orange-600 mb-2">Most Popular</div>}
              <div className="font-bold text-sm" style={{ color: t.color }}>{t.name}</div>
              <div className="text-xl font-black my-1" style={{ color: ACCENT_DARK }}>{t.price}</div>
              <div className="text-xs text-slate-500 mb-1">{t.listings} listings/mo</div>
              <div className="text-xs text-slate-400 mb-4 leading-relaxed">{t.desc}</div>
              {subscription?.tier === t.key ? (
                <div className="w-full py-2 text-center text-xs font-bold text-orange-600 bg-orange-50 rounded-lg">
                  Current Plan
                </div>
              ) : (
                <button onClick={() => onSubscribe(t.key)} disabled={!!subscribing}
                  data-testid={`seller-subscribe-${t.key}`}
                  className="w-full py-2 rounded-lg text-xs font-bold text-white transition-all hover:opacity-90 disabled:opacity-60"
                  style={{ background: t.color }}>
                  {subscribing === t.key ? 'Redirecting…' : active ? 'Switch Plan' : 'Choose Plan'}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ListingsTab({ listings, subscription, onOpenModal, onDelete, onStatusChange, onEdit, loading }) {
  const active = subscription?.status === 'active';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black" style={{ color: ACCENT_DARK }}>My Home</h2>
          <p className="text-slate-500 text-sm mt-1">Your listed properties and performance</p>
        </div>
        {active && (
          <button onClick={onOpenModal} data-testid="seller-create-listing-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90"
            style={{ background: ACCENT }}>
            <Plus size={16} />List My Home
          </button>
        )}
      </div>

      {!active ? (
        <div className="p-12 rounded-2xl border border-dashed border-orange-200 bg-orange-50 text-center">
          <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-3">
            <Lock size={20} className="text-orange-400" />
          </div>
          <p className="font-bold text-slate-700">Subscribe to list your home</p>
          <p className="text-sm text-slate-400 mt-1">Choose a plan from the Dashboard to start attracting matched buyers</p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-orange-300" />
        </div>
      ) : listings.length === 0 ? (
        <div className="p-12 rounded-2xl border border-dashed border-orange-200 bg-orange-50 text-center">
          <Home size={36} className="mx-auto text-orange-200 mb-3" />
          <p className="font-bold text-slate-700">No listings yet</p>
          <p className="text-sm text-slate-400 mt-1 mb-4">List your first property to attract matched buyers</p>
          <button onClick={onOpenModal} data-testid="seller-create-first-listing-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white hover:opacity-90 transition-all"
            style={{ background: ACCENT }}>
            <Plus size={14} />List My Home
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {listings.map(l => (
            <ListingCard key={l.id} listing={l} onDelete={onDelete} onStatusChange={onStatusChange} onEdit={onEdit} />
          ))}
        </div>
      )}
    </div>
  );
}

function LeadsTab({ leads, loading }) {
  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-black" style={{ color: ACCENT_DARK }}>Buyer Leads</h2>
        <p className="text-slate-500 text-sm mt-1">Click a lead to reveal buyer contact info</p>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-orange-300" />
        </div>
      ) : leads.length === 0 ? (
        <div className="p-12 rounded-2xl border border-dashed border-slate-300 bg-slate-50 text-center">
          <Users size={36} className="mx-auto text-slate-300 mb-3" />
          <p className="font-bold text-slate-600">No leads yet</p>
          <p className="text-sm text-slate-400 mt-1">Once buyers match with your home and reach out, they appear here with match scores</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {leads.map((lead, i) => <LeadCard key={lead.id || i} lead={lead} />)}
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function SellerDashboard({ tab }) {
  const { user, authFetch } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [stats, setStats]       = useState({ listing_count: 0, lead_count: 0, total_views: 0, avg_match_score: 0 });
  const [listings, setListings] = useState([]);
  const [leads, setLeads]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [subscribing, setSubscribing] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [toast, setToast] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3200); };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [subR, statsR, listR, leadR] = await Promise.all([
        authFetch(`${API}/api/subscriptions/status`),
        authFetch(`${API}/api/agent/stats`),
        authFetch(`${API}/api/agent/listings`),
        authFetch(`${API}/api/agent/leads`),
      ]);
      if (subR.ok)   setSubscription(await subR.json());
      if (statsR.ok) setStats(await statsR.json());
      if (listR.ok)  setListings(await listR.json());
      if (leadR.ok)  setLeads(await leadR.json());
    } catch (e) { console.error('SellerDashboard load error', e); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, []);

  // ─── Stripe return: poll status after redirect ────────────────────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;
    window.history.replaceState({}, '', window.location.pathname);
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await authFetch(`${API}/api/stripe/checkout/status/${sessionId}`);
        if (r.ok) {
          const data = await r.json();
          if (data.payment_status === 'paid') {
            const t = data.tier || 'plan';
            showToast(`${t.charAt(0).toUpperCase() + t.slice(1)} plan activated!`);
            await loadAll();
            return;
          }
        }
      } catch (e) { console.error(e); }
      attempts++;
      if (attempts < 8) setTimeout(poll, 2000);
    };
    poll();
  }, []); // eslint-disable-line

  const handleSubscribe = async (tier) => {
    setSubscribing(tier);
    try {
      const r = await authFetch(`${API}/api/stripe/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier, origin: window.location.origin, path: '/seller' }),
      });
      if (r.ok) {
        const data = await r.json();
        window.location.href = data.url;
        return;
      } else {
        const err = await r.json();
        showToast(err.detail || 'Could not start checkout');
      }
    } catch (e) { console.error('Stripe error', e); showToast('Checkout failed. Please try again.'); }
    setSubscribing('');
  };

  const handleDeleteListing = async (id) => {
    const r = await authFetch(`${API}/api/listings/${id}`, { method: 'DELETE' });
    if (r.ok) { showToast('Listing deleted'); await loadAll(); }
  };

  const handleStatusChange = async (id, status) => {
    const r = await authFetch(`${API}/api/listings/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (r.ok) {
      setListings(prev => prev.map(l => l.id === id ? { ...l, status } : l));
      showToast(`Status updated to ${status.replace('_', ' ')}`);
    }
  };

  const handleEdit = (listing) => {
    setEditingListing(listing);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingListing(null);
  };

  return (
    <>
      <ListingWizard
        isOpen={showModal}
        onClose={handleModalClose}
        editListing={editingListing}
        onSuccess={() => {
          showToast(editingListing ? 'Listing updated!' : 'Listing published!');
          setEditingListing(null);
          loadAll();
        }}
      />

      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 right-4 z-50 flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold px-4 py-3 rounded-xl shadow-xl"
            data-testid="seller-toast">
            <CheckCircle size={15} className="text-emerald-400" />{toast}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 p-5 rounded-2xl flex items-center gap-4"
          style={{ background: 'linear-gradient(135deg, #7c2d12 0%, #c2410c 100%)' }}>
          <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: ACCENT }}>
            <KeyRound size={18} className="text-white" />
          </div>
          <div className="flex-1">
            <p className="text-white font-bold">Welcome, {user?.name}!</p>
            <p className="text-orange-200 text-xs mt-0.5">Seller Portal · List your home, attract matched buyers</p>
          </div>
          {subscription?.status === 'active' && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-orange-500/20 text-orange-200">
              <BadgeCheck size={12} />
              {subscription.tier?.charAt(0).toUpperCase() + subscription.tier?.slice(1)}
            </div>
          )}
        </div>

        {tab === 'dashboard' && (
          <DashboardTab stats={stats} subscription={subscription}
            onSubscribe={handleSubscribe} subscribing={subscribing} loading={loading} />
        )}
        {tab === 'listings' && (
          <ListingsTab listings={listings} subscription={subscription}
            onOpenModal={() => { setEditingListing(null); setShowModal(true); }}
            onDelete={handleDeleteListing}
            onStatusChange={handleStatusChange}
            onEdit={handleEdit}
            loading={loading} />
        )}
        {tab === 'leads' && <LeadsTab leads={leads} loading={loading} />}
      </div>
    </>
  );
}
